package dev.lingvo.site.components.layouts

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.site.components.AppShell
import dev.lingvo.site.components.AuthGuard
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LEmptyStateIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LoadingState
import dev.lingvo.site.ds.LRouteTransition
import dev.lingvo.site.ds.Space
import dev.lingvo.site.nav.AppRoute
import dev.lingvo.site.state.ProjectRouteContext
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import dev.lingvo.site.i18n.t

@Composable
@Layout
fun AppLayout(
    ctx: PageContext,
    content: @Composable ColumnScope.() -> Unit,
) {
    AuthGuard {
        val routeSlug = AppRoute.projectSlug(ctx.route.path)
        if (routeSlug == null) {
            AppLayoutContent(ctx, content)
        } else {
            ProjectRouteContext.Gate(
                routeSlug = routeSlug,
                loading = { ProjectRouteLoading() },
                refused = { refusal, onRetry ->
                    ProjectRouteRefusal(
                        reason = refusal.reason,
                        retryable = refusal.retryable,
                        onRetry = onRetry,
                        onBackToProjects = { ctx.router.tryRoutingTo("/app/projects") },
                    )
                },
            ) {
                AppLayoutContent(ctx, content)
            }
        }
    }
}

@Composable
private fun AppLayoutContent(
    ctx: PageContext,
    content: @Composable ColumnScope.() -> Unit,
) {
    AppShell {
        // Keyed on the path, so /app/projects -> /app/projects/new remounts and the enter
        // actually plays. The shell itself — sidebar, top bar, search — deliberately sits
        // outside this: navigation should move the view, not redraw the furniture around it.
        LRouteTransition(routeKey = ctx.route.path) {
            Column(Modifier.fillMaxWidth()) { content() }
        }
    }
}

@Composable
internal fun ProjectRouteLoading() {
    Column(Modifier.fillMaxWidth().padding(Space.XXL).gap(Space.LG)) {
        LoadingState(rows = 6, label = t("layouts.app_layout.verifying_project_workspace", "Verifying project workspace"))
    }
}

@Composable
internal fun ProjectRouteRefusal(
    reason: String,
    retryable: Boolean = false,
    onRetry: () -> Unit = {},
    onBackToProjects: () -> Unit,
) {
    Column(Modifier.fillMaxWidth().padding(Space.XXL)) {
        LEmptyState(
            title = t("layouts.app_layout.this_project_cannot_be_opened_safely", "This project cannot be opened safely"),
            description = reason,
            modifier = Modifier.fillMaxWidth(),
            icon = LEmptyStateIcon.FORBIDDEN,
        ) {
            Column(Modifier.gap(Space.SM)) {
                if (retryable) {
                    LButton(t("layouts.app_layout.retry", "Retry"), onRetry)
                }
                LButton(
                    t("layouts.app_layout.back_to_projects", "Back to projects"),
                    onBackToProjects,
                    variant = LButtonVariant.GHOST,
                )
            }
        }
    }
}
